import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { paintBuildingAt } from "../../../lib/engine";
import { refHeight } from "../../../lib/paint";
import { D, JAKARTA } from "../ui";
import { Eye, Moon, Sun, X } from "lucide-react";

export function CircleThumb({ def }) {
  const ref = useRef(null);
  useEffect(() => {
    const cv = ref.current;
    if (!cv || !def) return;
    const S = 96; cv.width = S; cv.height = S;
    const ctx = cv.getContext("2d");
    ctx.clearRect(0, 0, S, S);
    const zoom = def.category === "Karakter" ? 3.7 : def.category === "Kendaraan" ? 1.5 : Math.min(0.8, 60 / ((def.w + def.h) * 32));
    try { paintBuildingAt(ctx, def, S / 2, S * 0.76, zoom); } catch { /* abaikan */ }
  }, [def]);
  return (
    <span style={{ width: 36, height: 36, borderRadius: "50%", overflow: "hidden", display: "inline-block", background: "#eef2f7", flex: "none" }}>
      <canvas ref={ref} style={{ width: "100%", height: "100%" }} />
    </span>
  );
}

// Preview lukisan aset di canvas (dipakai kartu, baris list, dan modal detail)
export function AssetPreview({ def, draftCode, height = 140 }) {
  const ref = useRef(null);
  useEffect(() => {
    const cv = ref.current;
    if (!cv || !def) return;
    const W = 300, H = height;
    cv.width = W; cv.height = H;
    const ctx = cv.getContext("2d");
    const g = ctx.createLinearGradient(0, 0, 0, H);
    g.addColorStop(0, "#eef4fa");
    g.addColorStop(1, "#e0ebf4");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, W, H);
    const d = draftCode ? { ...def, bid: def.bid + "__draft", paintCode: draftCode } : def;
    const footW = (d.w + d.h) * 64, footH = (d.w + d.h) * 16;
    const tall = refHeight(d.floors ?? 1, d.w, d.h, 1) * 1.12 + footH + 16;
    // Aset bergerak (Kendaraan/Karakter) berskala unit kecil → zoom tetap agar terbaca
    const isMover = d.category === "Karakter" || d.category === "Kendaraan";
    const zoom = d.category === "Karakter" ? 6 : d.category === "Kendaraan" ? 2.2 : Math.min(0.95, (W * 0.86) / footW, (H * 0.78) / tall);
    try { paintBuildingAt(ctx, d, W / 2, H * (isMover ? 0.86 : 0.8), zoom, { time: 1 }); } catch { /* abaikan */ }
  }, [def, draftCode, height]);
  return <canvas ref={ref} style={{ width: "100%", height, display: "block" }} />;
}

export const statusOf = (b) => b.has_draft
  ? ["Draf Baru", D.blue, D.blueSoft]
  : b.paintCode ? ["Terlukis", D.chipInk, D.chip] : ["Perlu AI", "#b45309", "#fde8d8"];

// Modal preview besar versi aset (aktif / draf / varian) dengan toggle siang/malam.
export function PreviewModal({ def, draftCode, label, onClose }) {
  const [night, setNight] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const cv = ref.current;
    if (!cv || !def) return;
    const W = 640, H = 480;
    cv.width = W; cv.height = H;
    const ctx = cv.getContext("2d");
    const g = ctx.createLinearGradient(0, 0, 0, H);
    if (night) { g.addColorStop(0, "#1a2238"); g.addColorStop(1, "#0e1424"); }
    else { g.addColorStop(0, "#eef4fa"); g.addColorStop(1, "#e0ebf4"); }
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, W, H);
    const d = draftCode ? { ...def, bid: def.bid + "__pv", paintCode: draftCode } : def;
    // Zoom menyesuaikan lebar tapak di layar ((w+h)*64px) DAN tinggi bangunan agar muat utuh
    const footW = (d.w + d.h) * 64, footH = (d.w + d.h) * 16;
    const tall = refHeight(d.floors ?? 1, d.w, d.h, 1) * 1.12 + footH + 24;
    const zoom = d.category === "Karakter" ? 12.8 : d.category === "Kendaraan" ? 4.6 : Math.min(1.7, (W * 0.86) / footW, (H * 0.78) / tall);
    try { paintBuildingAt(ctx, d, W / 2, H * 0.82, zoom, { night: night ? 1 : 0, time: 1 }); } catch { /* abaikan */ }
  }, [def, draftCode, night]);
  // Portal ke body: kartu aset punya transform saat hover, yang "menangkap" position:fixed modal
  return createPortal(
    <div data-testid="preview-modal" onClick={onClose}
      style={{ position: "fixed", inset: 0, zIndex: 200, background: "rgba(15,18,30,0.6)", backdropFilter: "blur(5px)", display: "grid", placeItems: "center", padding: 20 }}>
      <div onClick={e => e.stopPropagation()}
        style={{ width: "min(680px, 100%)", background: "#f8f6ee", borderRadius: 24, padding: "18px 20px", boxShadow: "0 24px 70px rgba(0,0,0,0.4)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ width: 32, height: 32, borderRadius: 10, background: D.blueSoft, display: "grid", placeItems: "center" }}><Eye size={15} color={D.blue} /></span>
          <b style={{ flex: 1, fontSize: 15, color: D.ink }}>{def.name}{label ? ` — ${label}` : ""}</b>
          <button data-testid="preview-night-toggle" onClick={() => setNight(n => !n)}
            style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 14px", borderRadius: 99, border: "1.5px solid #ece9dc", background: night ? "#1a2238" : "#fff", color: night ? "#fde68a" : D.ink, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: JAKARTA }}>
            {night ? <Moon size={13} /> : <Sun size={13} />} {night ? "Malam" : "Siang"}
          </button>
          <button data-testid="preview-close" onClick={onClose}
            style={{ width: 32, height: 32, borderRadius: "50%", border: "1.5px solid #e3dfd0", background: "#fff", display: "grid", placeItems: "center", cursor: "pointer", color: D.ink }}><X size={14} /></button>
        </div>
        <div style={{ marginTop: 14, borderRadius: 16, overflow: "hidden", boxShadow: "0 6px 18px rgba(20,20,20,0.08)" }}>
          <canvas ref={ref} style={{ width: "100%", height: 420, display: "block" }} />
        </div>
      </div>
    </div>
  , document.body);
}
